import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import moment = require('moment');
import { Observable, Subject } from 'rxjs';

import { PromoService } from '../promo.service';
import { FormsService } from '../../../shared/forms.service';
import { InitjsService } from '../../../shared/initjs.service';
import { DatetimepickerComponent } from '../../../shared/datetimepicker/datetimepicker.component';
import { HttpService } from '../../../shared/http.service';
import { PromoRequest, PromoResponse, BookResponse, TagResponse } from '@builtbybros/al-model';
import { AlertService } from '../../../shared/alert.service';
import { QueryParam } from '../../../shared/query-param';
import { PromoAssignComponent } from '../../promo-assign/promo-assign.component';

@Component({
  selector: 'app-promo-form',
  templateUrl: './promo-form.component.html',
  styleUrls: ['./promo-form.component.scss']
})
export class PromoFormComponent implements OnInit {
  promoId: number;
  typePercentage = true;
  itemIds: number[] = [];
  promoForm: FormGroup;
  filledData = false;
  applyTo: number;
  booksSelected: BookResponse.Book[] = [];
  tagsSelected: TagResponse.Tag[] = [];
  msg = {
    create: 'Promoción creada correctamente',
    itemAssign: 'Items asignados correctamente',
    itemError: 'Hubo un error al asignar los items',
    update: 'Promoción actualizada correctamente',
  };

  // Search
  searchResultItems: Observable<BookResponse.BookExcerpt[] | TagResponse.Tag[]>;
  searching = false;

  private searchTerms = new Subject<string>();

  @ViewChild(DatetimepickerComponent)
  private datetimepicker: DatetimepickerComponent;

  @ViewChild(PromoAssignComponent)
  private promoAssign: PromoAssignComponent;

  promoUrl = '/Promo/';
  promoItemUrl = '/PromoItem/';

  constructor(private router: Router,
              private route: ActivatedRoute,
              private fb: FormBuilder,
              private formsService: FormsService,
              private httpService: HttpService,
              private promoService: PromoService,
              private initJsService: InitjsService,
              private alertService: AlertService) {
    this.createFormGroup();
  }

  ngAfterViewInit() {
    this.initJsService.initJs();
  }

  private onSetTypeCalled(isPercentage: boolean) {
    if(!isPercentage) {
      this.typePercentage = null;
    }
    this.setType(isPercentage);
  }

  ngOnInit() {
    this.setType(true);
    this.route.params.subscribe((params: Params) => {
      if (params['promoId']) {
        // Update promo
        // Get the promo with id = PromoId
        this.promoId = +params['promoId'];
        const responseExpected = new PromoResponse.Promo();

        this.httpService.get(this.promoUrl, this.promoId, responseExpected)
          .subscribe(
            promoResponse => {
              this.promoForm.patchValue(promoResponse);
              this.typePercentage = (promoResponse.percentage !== null && promoResponse.percentage !== 0) && (promoResponse.percentage !== undefined && promoResponse.percentage !== 0);
              this.setType(this.typePercentage);
              const date = moment(promoResponse.expirationDate).format('DD/MM/YYYY');
              this.datetimepicker.selectDate(date);
              this.filledData = true;
              this.applyTo = promoResponse.applyTo;
            },
            error => {
              this.alertService.error(error);
            }
          );
      }
    });
    this.initJsService.initJs();

    // Search
    this.searchResultItems = this.searchTerms
      .debounceTime(300)        // wait 300ms after each keystroke before considering the term
      .distinctUntilChanged()   // ignore if next search term is same as previous
      .do(() => this.searching = true)
      .switchMap(term => term ? this.searchItem(term) : Observable.of<BookResponse.BookExcerpt[] | TagResponse.Tag[]>([]))
      .catch(error => {
        this.alertService.error(error);
        return Observable.of<BookResponse.BookExcerpt[] | TagResponse.Tag[]>([]);
      })
      .do(() => this.searching = false);
  }

  // SEARCH
  search(term: string): void {
    this.searchTerms.next(term);
  }


  private searchItem(query: string): Observable<BookResponse.BookExcerpt[] | TagResponse.Tag[]> {
    const params = [
      new QueryParam('pagination', '10'),
      new QueryParam('query', query),
    ];

    const searchUrl = this.applyTo === 1 ? '/TagSearch' : '/Book/All';
    const resultObservable: Observable<BookResponse.All | TagResponse.All> = this.applyTo === 1 ?
      this.httpService.getWithParams(searchUrl, params, TagResponse.All) :
      this.httpService.getWithParams(searchUrl, params, BookResponse.All);

    return resultObservable
      .map(
        itemResponse => itemResponse.objects,
        _error => [],
      );
  }

  submitForm() {
    if (this.formsService.validateFormAndUpdateErrors(this.promoForm)) {

      const expectedResponse = new PromoResponse.Promo();
      if (this.promoId) {
        const promo = new PromoRequest.Update();
        // Update promo
        promo.parse(this.promoForm.value);
        promo.roles = this.promoForm.get('roles').value;
        promo.percentage = this.typePercentage ? promo.percentage : 0;
        promo.fixed = this.typePercentage ? 0 : promo.fixed;
        this.httpService.patch(this.promoId, this.promoUrl, promo, expectedResponse)
          .subscribe(
            createResponse => {
              this.filledData = true;
              this.applyTo = createResponse.applyTo;
              this.alertService.success(this.msg['update']);
            },
            error => {
              this.alertService.error(error);
            }
          );
      } else {
        // Create promo
        const promo = new PromoRequest.Create();
        promo.parse(this.promoForm.value);
        promo.roles = this.promoForm.get('roles').value;
        promo.percentage = this.typePercentage ? promo.percentage : null;
        promo.fixed = this.typePercentage ? null : promo.fixed;
        this.httpService.post(this.promoUrl, promo, expectedResponse)
          .subscribe(
            createResponse => {
              this.filledData = true;
              this.applyTo = createResponse.applyTo;
              this.alertService.success(this.msg['create']);
            },
            error => {
              this.alertService.error(error);
            }
          );
      }
    }
    return;
  }

  switchApplyTo(event) {
    this.applyTo = +event.target.value;
    if (this.promoAssign) {
      this.promoAssign.cleanItems();
      if (this.applyTo === 1) {
        this.promoAssign.getTags();
      } else if (this.applyTo === 2) {
        this.promoAssign.getBooks();
      }
    }
  }

  assignPromoItem(value) {
    const result: PromoRequest.Assign = new PromoRequest.Assign();
    result.promoId = this.promoId;
    result.itemIds = value;
    this.promoService.assignPromoItems(this.promoId, result)
      .subscribe(
        createResponse => {
          this.alertService.success(this.msg['itemAssign']);
        },
        error => {
          this.alertService.error(this.msg['itemError']);
        }
      );
  }

  addItem(value) {
    this.promoAssign.addItem(+value);
  }

  assignItem(value) {
    this.assignPromoItem(value);
  }

  setType(isPercentage: boolean) {
    this.typePercentage = isPercentage;
    if (isPercentage) {
      this.promoForm.controls['fixed'].disable();
      this.promoForm.controls['percentage'].enable();
    } else {
      this.promoForm.controls['fixed'].enable();
      this.promoForm.controls['percentage'].disable();
    }

  }

  setDate(dateString: string) {
    this.promoForm.controls['expirationDate'].setValue(moment(dateString, 'DD-MM-YYYY').toDate());
  }

  cleanSearch() {
    this.searchTerms.next('');
  }

  private createFormGroup() {
    this.promoForm = this.fb.group({
      active: [
        true,
        null
      ],
      applyTo: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      exclusive: [
        true,
        null
      ],
      expirationDate: [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(50)
        ])
      ],
      fixed: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^\d+$/),
          Validators.maxLength(50)
        ])
      ],
      name: [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(50)
        ])
      ],
      percentage: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern('^[1-9][0-9]?$|^100$'),
          Validators.maxLength(4)
        ])
      ],
      roles: [
        true,
        Validators.compose([
          Validators.required
        ])
      ]
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/promos/shared/promo-form/promo-form.component.ts